import React from 'react';
import {
  Stack, Button, IconButton, ToggleButtonGroup, ToggleButton,
  Divider, Tooltip,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadIcon from '@mui/icons-material/Upload';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import SettingsIcon from '@mui/icons-material/Settings';
import CloudIcon from '@mui/icons-material/Cloud';
import { useTranslation } from 'react-i18next';

export type ViewMode = 'list' | 'grid';

interface Props {
  canCreate: boolean;
  canUpdate: boolean;
  readonly: boolean;
  canUp: boolean;
  view: ViewMode;
  selectedCount: number;
  onUpload: () => void;
  onNewFolder: () => void;
  onUp: () => void;
  onRefresh: () => void;
  onViewChange: (v: ViewMode) => void;
  onBatchDownload: () => void;
  onBatchDelete: () => void;
  onBatchMove: () => void;
  onWebdavSettings: () => void;
  webdavEnabled: boolean | null;
  onAdminQuota?: () => void;
}

export default function Toolbar({
  canCreate, canUpdate, readonly, canUp, view, selectedCount,
  onUpload, onNewFolder, onUp, onRefresh, onViewChange,
  onBatchDownload, onBatchDelete, onBatchMove, onWebdavSettings, webdavEnabled, onAdminQuota,
}: Props) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={1} alignItems="center"
           sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
      <IconButton size="small" onClick={onUp} disabled={!canUp}>
        <ArrowUpwardIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={onRefresh}>
        <RefreshIcon fontSize="small" />
      </IconButton>
      <Divider orientation="vertical" flexItem />
      <Button size="small" startIcon={<UploadIcon />}
              disabled={readonly || !canCreate} onClick={onUpload}>
        {t('myDrive.selectFiles', '選擇檔案')}
      </Button>
      <Button size="small" startIcon={<CreateNewFolderIcon />}
              disabled={readonly || !canCreate} onClick={onNewFolder}>
        {t('myDrive.newFolder', '新增資料夾')}
      </Button>

      {/* 批次下載：read 操作，唯讀目錄（system/mail）也可用 */}
      {selectedCount > 0 && (
        <>
          <Divider orientation="vertical" flexItem />
          <Button size="small" startIcon={<DownloadIcon />}
                  onClick={onBatchDownload}>
            {t('myDrive.batchDownload', '下載所選')} ({selectedCount})
          </Button>
        </>
      )}

      {/* 批次操作：僅 selectedCount > 0 時顯示 */}
      {selectedCount > 0 && !readonly && (
        <>
          <Divider orientation="vertical" flexItem />
          <Button size="small" startIcon={<DeleteIcon />} color="error"
                  onClick={onBatchDelete}>
            {t('myDrive.batchDelete', '刪除所選')} ({selectedCount})
          </Button>
          <Button size="small" startIcon={<DriveFileMoveIcon />}
                  onClick={onBatchMove}>
            {t('myDrive.batchMove', '移動所選')} ({selectedCount})
          </Button>
        </>
      )}

      <span style={{ flex: 1 }} />
      <ToggleButtonGroup value={view} exclusive size="small"
                         onChange={(_, v) => v && onViewChange(v)}>
        <ToggleButton value="list"><ViewListIcon fontSize="small" /></ToggleButton>
        <ToggleButton value="grid"><ViewModuleIcon fontSize="small" /></ToggleButton>
      </ToggleButtonGroup>
      <Tooltip title={t('myDrive.webdavSettings', 'WebDAV 設定')}>
        <IconButton size="small" onClick={onWebdavSettings}>
          <CloudIcon
            fontSize="small"
            sx={{ color: webdavEnabled ? 'success.main' : 'inherit' }}
          />
        </IconButton>
      </Tooltip>
      {onAdminQuota && canUpdate && (
        <Tooltip title={t('myDrive.adminLink', '管理配額')}>
          <IconButton size="small" onClick={onAdminQuota}>
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}
